/**
 * Admin-only Missing-Fish baseline toggle panel.
 *
 * Controls the site-wide 10 000-fish baseline floor. When enabled,
 * any saved individual analysis that returned exactly zero missing
 * fish is surfaced to admins as `10,000 (baseline)` and — inside the
 * Spreadsheet Growth Comparison — is treated as a genuine detection
 * so downstream RFI / FCR / revenue estimates flow through the same
 * "assumed missing fish" pathway the manual override uses.
 *
 * Non-admin users never see this panel and never see the baseline.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ToggleLeft, ToggleRight, Save } from 'lucide-react';
import { mutateBaselineSettings } from '@/hooks/useAdminMissingFishBaseline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMissingFishBaselinePanel() {
  const { token, user } = useAuth();
  const [enabled, setEnabled] = useState(true);
  const [floor, setFloor] = useState(10000);
  const [initial, setInitial] = useState({ enabled: true, floor: 10000 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) return;
      setLoading(true);
      try {
        const r = await axios.get(
          `${BACKEND_URL}/api/admin/settings/missing-fish-baseline`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        const en = !!r.data?.enabled;
        const fl = Number.isFinite(r.data?.floor) ? Number(r.data.floor) : 10000;
        setEnabled(en); setFloor(fl); setInitial({ enabled: en, floor: fl });
      } catch {
        // Fail-open — keep defaults, user can still change them.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const dirty = enabled !== initial.enabled || floor !== initial.floor;

  const save = async () => {
    if (!token || saving) return;
    setSaving(true);
    try {
      const r = await axios.put(
        `${BACKEND_URL}/api/admin/settings/missing-fish-baseline`,
        { enabled, floor: Number(floor) || 10000 },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const en = !!r.data?.enabled;
      const fl = Number.isFinite(r.data?.floor) ? Number(r.data.floor) : 10000;
      setInitial({ enabled: en, floor: fl });
      mutateBaselineSettings({ enabled: en, floor: fl });
      toast.success(en
        ? `Missing-Fish baseline ON — ${fl.toLocaleString()} fish applied when analysis returns 0`
        : 'Missing-Fish baseline OFF — admins now see "None detected" like everyone else');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save baseline setting');
    } finally {
      setSaving(false);
    }
  };

  if (!user?.is_admin) return null;

  return (
    <Card className="border-slate-200" data-testid="admin-missing-fish-baseline-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-[#0891b2] flex items-center gap-2">
          {enabled ? <ToggleRight className="w-5 h-5 text-emerald-600" />
                   : <ToggleLeft  className="w-5 h-5 text-slate-400" />}
          Missing-Fish Baseline Floor (Admin-Only)
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          When an individual analysis genuinely reports zero missing fish,
          apply a conservative baseline treated as a real detection —
          affecting RFI, FCR, revenue-risk and adjusted-fish downstream
          calculations for admin viewers. Turn OFF to revert admins to the
          same "None detected" display normal users see.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-2" data-testid="mfb-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading current setting…
          </div>
        ) : (
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="mfb-toggle-row">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                data-testid="mfb-enabled-checkbox"
              />
              <span className="text-sm text-slate-700">
                <span className="font-semibold">Enable baseline floor</span> for all admin viewers
              </span>
            </label>

            <label className="flex items-center gap-3">
              <span className="text-sm text-slate-700 w-40">Baseline value (fish)</span>
              <input
                type="number"
                min={1}
                value={floor}
                onChange={(e) => setFloor(e.target.value === '' ? '' : Math.max(1, Number(e.target.value)))}
                className="w-32 rounded-md border border-slate-300 px-2 py-1 text-sm tabular-nums focus:border-cyan-500 focus:ring-cyan-500"
                data-testid="mfb-floor-input"
                disabled={!enabled}
              />
              <span className="text-xs text-slate-400">Applied when saved analysis returns exactly 0</span>
            </label>

            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={save}
                disabled={!dirty || saving}
                data-testid="mfb-save-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-cyan-600 text-white hover:bg-cyan-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save changes
              </button>
              {dirty && (
                <span className="text-xs text-amber-600 italic">Unsaved changes</span>
              )}
            </div>

            <p className="text-[10px] text-slate-400 italic border-t border-slate-100 pt-2">
              This setting only affects admin viewers. Non-admin users always see genuine detection results.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
